import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api-client';
import { MutationConfig } from '@/lib/tanstack-query';
import { getParticipationQueryOptions } from './get-participation';
import { listParticipationsQueryOptions } from './list-participations';
import { Participation } from '@/types/api/sale';

export const UpdateParticipationCollectedInputSchema = z.object({
  collected: z.boolean(),
});

export type UpdateParticipationCollectedInput = z.infer<typeof UpdateParticipationCollectedInputSchema>;

export const updateParticipationCollected = async ({
  data,
  saleId,
  userId,
}: {
  data: UpdateParticipationCollectedInput;
  saleId: string;
  userId: string;
}): Promise<void> => {
  return (await api.put(`/sales/${saleId}/participations/${userId}/collected`, data)).data;
};

type UpdateParticipationCollectedOptions = {
  mutationConfig?: MutationConfig<typeof updateParticipationCollected>;
};

export const useUpdateParticipationCollected = ({ mutationConfig }: UpdateParticipationCollectedOptions = {}) => {
  const { onSuccess, onMutate, onError, onSettled, ...restConfig } = mutationConfig || {};

  return useMutation({
    onMutate: async (variables, context) => {
      const listKey = listParticipationsQueryOptions(variables.saleId).queryKey;
      const getKey = getParticipationQueryOptions(variables.saleId, variables.userId).queryKey;

      await context.client.cancelQueries({ queryKey: listKey });

      const [[, prevList]] = context.client.getQueriesData<Participation[]>({ queryKey: listKey });
      const [[, prevGet]] = context.client.getQueriesData<Participation>({ queryKey: getKey });

      context.client.setQueriesData<Participation[]>({ queryKey: listKey }, (old) => {
        if (!old) return old;
        const index = old.findIndex((p) => p.userId === variables.userId);
        if (index === -1) return old;
        return old.map((p, i) => (i === index ? { ...p, collected: variables.data.collected } : p));
      });

      context.client.setQueriesData<Participation>({ queryKey: getKey }, (old) =>
        old ? { ...old, collected: variables.data.collected } : old,
      );

      return { prevList, prevGet, getKey, listKey };
    },

    onError: (error, variables, onMutateResult, context) => {
      if (onMutateResult) {
        context.client.setQueriesData({ queryKey: onMutateResult.getKey }, onMutateResult.prevGet);
        context.client.setQueriesData({ queryKey: onMutateResult.listKey }, onMutateResult.prevList);
      }

      onError?.(error, variables, onMutateResult, context);
    },

    onSettled: async (data, error, variables, onMutateResult, context) => {
      const listKey = listParticipationsQueryOptions(variables.saleId).queryKey;
      const getKey = getParticipationQueryOptions(variables.saleId, variables.userId).queryKey;

      await context.client.invalidateQueries({ queryKey: listKey });
      await context.client.invalidateQueries({ queryKey: getKey });

      onSettled?.(data, error, variables, onMutateResult, context);
    },

    onSuccess: (...args) => {
      onSuccess?.(...args);
    },

    ...restConfig,
    mutationFn: updateParticipationCollected,
  });
};
